/**
 * Date / timezone helpers.
 *
 * MoneyOne clients operate in IST (a fixed UTC+05:30 offset). Date pickers hand back a
 * Date at the browser's local midnight, so to serialize a picked calendar day to an
 * absolute instant we (a) read the day the user actually selected via its LOCAL fields
 * (getFullYear/getMonth/getDate) — the day they see regardless of browser timezone — and
 * (b) build the UTC instant for that day's IST boundary. This stays correct even when a
 * tester's / user's browser is not in IST.
 */

/** IST is a fixed UTC+05:30 offset. */
export const IST_OFFSET_MS = (5 * 60 + 30) * 60 * 1000;

/**
 * Serialize a picked calendar day to the UTC ISO instant of that day's IST start
 * (00:00:00.000) or end (23:59:59.999). Use for inclusive IST date ranges sent to the
 * backend — e.g. "20 Jun -> 21 Jul" maps to [start of 20 Jun IST, end of 21 Jul IST].
 *
 * NOTE: do NOT use setUTCHours on the Date directly — for a local-midnight IST Date the
 * UTC day is the previous day, so it would land a day early. Keying off the local
 * calendar fields is what makes this browser-timezone independent.
 *
 * @example toISTDayBoundaryISO(new Date(2026, 6, 21), 'end') // "2026-07-21T18:29:59.999Z"
 */
export const toISTDayBoundaryISO = (d: Date, edge: 'start' | 'end'): string => {
  const utcForIstWallClock =
    edge === 'start'
      ? Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0)
      : Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
  return new Date(utcForIstWallClock - IST_OFFSET_MS).toISOString();
};
